import { prop } from 'ramda';
import React, { useCallback } from 'react';
import { Button } from 'js/components/common/Button';
import { componentService } from 'js/services/component.service';
import { withComponent } from 'js/components/hocs/withComponent';
import { useComponentConfig, useComponentMethods, useComponentProps } from 'js/hooks/component/';

import * as Types from './types';
import * as actions from './store/actions';
import { withDynamicReducer, useDynamicReducerDispatch, useDynamicReducerSelector } from './store';

import style from './ReduxComponentExample.scss';

export const ReduxComponentExampleBare: React.FC<Types.IReduxComponentExampleProps> = () => {
    // in case you have multiple ReduxComponentExample components on a single page
    // you might use different dispatches.
    // useDynamicReducerDispatch - is used to dispatch event to a single component (itself)
    // redux useDispatch - is used to dispatch event to all components of that kind
    const dynamicReducerDispatch = useDynamicReducerDispatch();

    // if you use withDynamicReducer HOC you need to use DynamicReducerSelector
    // this is because redux store for such component is extended with component store as soon as component is shown
    // and deleted from the redux global store after component is removed from DOM
    const buttonText = useDynamicReducerSelector(prop('buttonText'));

    // withComponent HOC allows your component to show similar messages across all components
    // get access to parsed portlet config or replacer attributes
    // show component for loggedIn users only
    // it also has some useful method as showComponentError and showComponentMessage
    // to show messages instead of the whole portlet
    const { showComponentError, showComponentMessage } = useComponentMethods();

    // this hook allows you to get replacer attributes
    const { testprop } = useComponentProps<Types.IReduxComponentExampleProps>();

    // this hook allows you to get portlet config or replacer "config" attribute
    // so it means that you can use any portlet as a replacer and vise versa
    // just don't forget to register this component as both webContent and Page components
    const { configMessage } = useComponentConfig<Types.IReduxComponentExampleConfig>();

    const handleReducerTestButtonClick = useCallback(() => {
        dynamicReducerDispatch(actions.updateButtonText('Reducer works'));
    }, [dynamicReducerDispatch]);

    const handleComponentErrorButtonClick = useCallback(() => {
        showComponentError('error message works');
    }, [showComponentError]);

    const handleComponentMessageButtonClick = useCallback(() => {
        showComponentMessage('Component regular message works');
    }, [showComponentMessage]);

    return (
        <>
            <p>{`Add attribute testProp="prop message" to this component within a WC in order to see its value here: ${testprop}`}</p>
            <p>{`Add attribute config="{'configMessage': 'config message'}" to this component in WC in order to see its value here: ${configMessage}`}</p>
            <Button
                type="button"
                className={style.button}
                onClick={handleReducerTestButtonClick}
            >
                {`${buttonText}`}
            </Button>
            <Button
                type="button"
                className={style.button}
                onClick={handleComponentErrorButtonClick}
            >
                {'test error message'}
            </Button>
            <Button
                type="button"
                className={style.button}
                onClick={handleComponentMessageButtonClick}
            >
                {'test regular message'}
            </Button>
        </>
    );
};

export const ReduxComponentExample = withComponent()(withDynamicReducer()(ReduxComponentExampleBare));

componentService.registerWebContentComponents({ ReduxComponentExample });
